import i18n from '@/i18n';
import { format } from 'date-fns';
import { taskAPI, getIpeWorkspaceMode } from '@/utils/api';
import { trackPendingTask } from '@/utils/workbenchHistoryBadge';
import {
  DEFAULT_AI_COPYWRITING_LANGUAGE,
  getCopywritingLanguageLabel,
} from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import { allocateTaskIdentifiersLocal } from '@/pages/ipe/workbench/utils/taskIdGenerator';
import { createIpeWorkflowTask } from '@/pages/ipe/workbench/utils/ipeWorkflowTaskRecord';
import { resolveImg2ImgReferenceForApi } from '@/pages/ipe/workbench/utils/img2imgPublicUrl';
import {
  clearAiCopywritingTaskCancel,
} from '@/pages/ipe/workbench/utils/aiCopywritingTaskCancel';
import { toIpeUserErrorMessage } from '@/pages/ipe/workbench/utils/errorHandler';
import { buildHistoryPanelRestoreSnapshot } from '@/pages/ipe/workbench/utils/workbenchHistoryPanelRestore';
import { extractFirstUrlsFromImageGroups } from '@/pages/ipe/workbench/utils/aiCopywritingUploadGroups';
import { stripImageFileExtension } from '@/utils/imageUrlUtils';

const OUTPUT_COUNT = 1;

function normalizeSources(sources = []) {
  return (sources || [])
    .map((s) => {
      const id = String(s?.id || '').trim();
      const url = String(s?.url || s?.previewUrl || s?.apiUrl || '').trim();
      if (!url) return null;
      return {
        id: id || `src-${Math.random().toString(36).slice(2, 8)}`,
        name: String(s?.name || id || '商品图').trim() || '商品图',
        url,
        previewUrl: String(s?.previewUrl || url).trim(),
      };
    })
    .filter(Boolean);
}

function normalizeMaxChars(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.max(1, Math.min(500, Math.floor(n)));
}

async function resolvePublicRefs(sources) {
  const list = Array.isArray(sources) ? sources : [];
  const resolvedList = await Promise.all(list.map(async (source) => {
    const resolved = await resolveImg2ImgReferenceForApi(source.url || source.previewUrl);
    if (!resolved) {
      throw new Error(i18n.t('workbench:ipeUtilError.imageUnresolvedNamed', { name: source.name }));
    }
    return {
      publicRef: resolved,
      preview: source.previewUrl || source.url,
    };
  }));
  return {
    publicRefs: resolvedList.map((row) => row.publicRef),
    previews: resolvedList.map((row) => row.preview),
  };
}

/** 将文案任务 ID 追加到上源主图/POD 任务 result.linkedAiCopywritingTaskIds */
async function appendLinkedAiCopywritingTaskId(sourceGoodsTaskId, copywritingTaskId) {
  const goodsId = String(sourceGoodsTaskId || '').trim();
  const cwId = String(copywritingTaskId || '').trim();
  if (!goodsId || !cwId) return;
  try {
    const res = await taskAPI.getById(goodsId);
    const goodsTask = res?.task || res?.data || res;
    if (!goodsTask) return;
    const prevResult = goodsTask.result && typeof goodsTask.result === 'object'
      ? goodsTask.result
      : {};
    const prev = Array.isArray(prevResult.linkedAiCopywritingTaskIds)
      ? prevResult.linkedAiCopywritingTaskIds.map(String).filter(Boolean)
      : [];
    if (prev.includes(cwId)) return;
    await taskAPI.update(goodsId, {
      result: {
        ...prevResult,
        linkedAiCopywritingTaskIds: [...prev, cwId],
      },
    });
  } catch (error) {
    console.warn('[aiCopywriting] 回写 linkedAiCopywritingTaskIds 失败', error);
  }
}

/**
 * 详情页联动 AI文案：本页创建任务并提交服务端队列，返回 taskId 供跳转并打开历史滑入面板。
 * @param {{ sourceGoodsTaskId?: string }} [params] 上源主图/POD 任务 id，用于导出时精确关联标题
 */
export async function createAndRunAiCopywritingTaskFromDetail({
  sources = [],
  prompt = '',
  language = DEFAULT_AI_COPYWRITING_LANGUAGE,
  maxChars,
  uploadMode = 'default',
  titlePresetId = '',
  titlePresetName = '',
  titlePresetContent = '',
  operatorName = '',
  sourceGoodsTaskId = '',
} = {}) {
  const workspaceMode = getIpeWorkspaceMode();
  const list = normalizeSources(sources);
  const apiPrompt = String(prompt || '').trim()
    || String(titlePresetContent || '').trim();
  const apiLanguage = String(language || DEFAULT_AI_COPYWRITING_LANGUAGE).trim()
    || DEFAULT_AI_COPYWRITING_LANGUAGE;
  const apiMaxChars = normalizeMaxChars(maxChars);
  const apiUploadMode = String(uploadMode || '').trim().toLowerCase() === 'custom' ? 'custom' : 'default';

  if (list.length === 0 && !apiPrompt) {
    throw new Error(i18n.t('workbench:ipeUtilError.selectImageOrCopyTemplate'));
  }
  if (list.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.selectImagesFirst'));
  }

  const { publicRefs, previews } = await resolvePublicRefs(list);
  const { taskId, subAccount } = allocateTaskIdentifiersLocal('aiCopywriting');
  const multiImageFromDetail = list.length > 1;
  const isCustom = apiUploadMode === 'custom' || multiImageFromDetail;

  // 多图（商品设计详情选中多张）：每组一张图 → 单任务多条文案输出
  let imageGroups = [];
  if (isCustom && publicRefs.length > 0) {
    const names = publicRefs.map((_, index) => {
      const rawName = String(list[index]?.name || '').trim();
      return stripImageFileExtension(rawName) || `图片 ${index + 1}`;
    });
    if (multiImageFromDetail) {
      imageGroups = publicRefs.map((url, index) => ({
        urls: [url],
        previews: [previews[index] || url],
        names: [names[index]],
        sourceIds: list[index]?.id ? [String(list[index].id)] : [],
      }));
    } else {
      imageGroups = [{
        urls: [...publicRefs],
        previews: [...previews],
        names,
      }];
    }
  }

  const extracted = isCustom && imageGroups.length > 0
    ? extractFirstUrlsFromImageGroups(imageGroups)
    : { urls: publicRefs, previews };
  const referenceUrls = isCustom ? extracted.urls : publicRefs;
  const referencePreviews = isCustom
    ? (extracted.previews.length > 0 ? extracted.previews : referenceUrls)
    : previews;
  const generationMode = referenceUrls.length > 0 ? 'vision' : 'text';
  const taskTotal = isCustom
    ? Math.max(1, imageGroups.length)
    : OUTPUT_COUNT;
  const outputCountForTask = multiImageFromDetail ? taskTotal : OUTPUT_COUNT;
  const imageIds = list.map((s) => String(s.id));
  const linkedSourceTaskId = String(sourceGoodsTaskId || '').trim();
  const initialResult = {
    prompt: apiPrompt,
    titlePresetId: titlePresetId ? String(titlePresetId) : undefined,
    titlePresetName: titlePresetName || undefined,
    titlePresetContent: titlePresetContent || undefined,
    referenceUrls,
    referencePreviews,
    generationMode,
    outputCount: outputCountForTask,
    language: apiLanguage,
    languageLabel: getCopywritingLanguageLabel(apiLanguage),
    maxChars: apiMaxChars,
    uploadMode: isCustom ? 'custom' : 'default',
    ...(isCustom ? { imageGroups } : {}),
    serverWorker: true,
    batchPhase: 'queued',
    operator: operatorName,
    operatorName,
    content: '',
    text: '',
    ...(linkedSourceTaskId ? { sourceGoodsTaskId: linkedSourceTaskId } : {}),
  };

  clearAiCopywritingTaskCancel(taskId);
  await createIpeWorkflowTask({
    id: taskId,
    type: 'aiCopywriting',
    name: 'AI文案任务',
    title: `AI文案 ${subAccount}`,
    subAccount,
    imageIds,
    previewUrl: referencePreviews[0] || previews[0] || '',
    status: 'processing',
    total: taskTotal,
    successCount: 0,
    failedCount: 0,
    result: initialResult,
  });
  trackPendingTask('aiCopywriting', taskId, workspaceMode);

  try {
    await taskAPI.submit(taskId);
    // 回写上游关联会拉取并整包更新商品设计大 result，放到提交成功后异步执行，缩短「带入中」
    void appendLinkedAiCopywritingTaskId(sourceGoodsTaskId, taskId);
  } catch (error) {
    try {
      await taskAPI.update(taskId, {
        status: 'failed',
        successCount: 0,
        failedCount: taskTotal,
        finishedAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        result: {
          ...initialResult,
          failureHint: toIpeUserErrorMessage(error, '提交失败'),
          batchPhase: 'failed',
          serverWorker: true,
        },
      });
    } catch {
      /* ignore */
    }
    throw error;
  }

  return {
    taskId,
    subAccount,
    restoreHistoryPanel: buildHistoryPanelRestoreSnapshot('aiCopywriting', {
      highlightTaskId: taskId,
    }),
  };
}

/** 文案任务终态 result → 标题正文（首条） */
export function extractAiCopywritingTitleFromTask(task) {
  const titles = extractAiCopywritingTitlesFromTask(task);
  return titles[0] || '';
}

function normalizeCopywritingTitleText(raw) {
  let text = String(raw || '').trim();
  if (!text) return '';
  text = text.replace(/^(?:商品标题|产品标题|标题|Title)\s*[:：]\s*/i, '').trim();
  text = text.replace(
    /^(?:方案|选项|Option|Variant)\s*[一二三四五六七八九十1-4]?\s*[:：.\-–—|]?\s*/i,
    '',
  ).trim();
  text = text.replace(/^["'“”‘’]+|["'“”‘’]+$/g, '').trim();
  return text;
}

/** 文案任务终态 → 按输出顺序的全部标题 */
export function extractAiCopywritingTitlesFromTask(task) {
  const result = task?.result && typeof task.result === 'object' ? task.result : {};
  const outputs = Array.isArray(result.outputs) ? result.outputs : [];
  const titles = [];
  for (const row of outputs) {
    const nested = row?.output && typeof row.output === 'object' ? row.output : null;
    const text = normalizeCopywritingTitleText(
      nested?.text || nested?.content || nested?.title
      || row?.output || row?.content || row?.title || '',
    );
    if (text) titles.push(text);
  }
  if (titles.length > 0) return titles;
  const content = normalizeCopywritingTitleText(result.content || result.text || result.title || '');
  return content ? [content] : [];
}
